// Iter 62cz (Rayan msg 2026-09-03) — Modal Admin To-Do.
// Deux modes : "create" (default) et "edit" (task fournie). Ajouts :
//   - Récurrence programmable (Aucune / Quotidienne / Hebdo / Mensuelle)
//   - Suppression via icône poubelle (mode edit)
//   - Changement d'assigné, importance, titre, description
//   - Tag `source: 'admin_todo'` sur toutes les créations pour isolation totale.
import { useState } from 'react';
import { toast } from 'sonner';
import { api } from '../lib/api';
import { Trash2 } from 'lucide-react';

const IMPORTANCE = [
  { id: 'urgent',    label: 'Urgent',      priority: 'high' },
  { id: 'important', label: 'Important',   priority: 'medium' },
  { id: 'normal',    label: 'Normal',      priority: 'low' },
];

const RECURRENCE = [
  { id: 'none',    label: 'Aucune' },
  { id: 'daily',   label: 'Quotidienne' },
  { id: 'weekly',  label: 'Hebdo' },
  { id: 'monthly', label: 'Mensuelle' },
];

const ASSIGNEES = [
  { id: 'rayan', label: 'Rayan' },
  { id: 'hugo',  label: 'Hugo'  },
];

function importanceFromTask(t) {
  const p = t?.priority || 'medium';
  if (p === 'high') return 'urgent';
  if (p === 'medium') return 'important';
  return 'normal';
}

export default function AdminTodoTaskDialog({ onClose, onSaved, task = null, onDeleted }) {
  const isEdit = !!task;
  const preselected = (() => {
    try { return localStorage.getItem('asa_new_task_assigned') || 'rayan'; }
    catch { return 'rayan'; }
  })();
  const [title, setTitle] = useState(task?.title || '');
  const [description, setDescription] = useState(task?.description || '');
  const [importance, setImportance] = useState(isEdit ? importanceFromTask(task) : 'normal');
  const [recurrence, setRecurrence] = useState(task?.recurring || 'none');
  const [assignee, setAssignee] = useState(
    isEdit
      ? (task?.assigned_to || 'rayan')
      : (ASSIGNEES.find((a) => a.id === preselected) ? preselected : 'rayan')
  );
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!title.trim()) { toast.error('Le nom de la tâche est requis'); return; }
    setBusy(true);
    try {
      const importanceCfg = IMPORTANCE.find((i) => i.id === importance) || IMPORTANCE[2];
      const payload = {
        title: title.trim(),
        description: description.trim(),
        priority: importanceCfg.priority,
        assigned_to: assignee,
        recurring: recurrence,
      };
      if (isEdit) {
        await api.patch(`/tasks/${task.id}`, payload);
        toast.success('Tâche modifiée');
      } else {
        await api.post('/tasks', {
          ...payload,
          stage: 'todo',
          event_type: 'task',
          kind: 'manual',
          source: 'admin_todo',
        });
        try { localStorage.removeItem('asa_new_task_assigned'); } catch { /* noop */ }
        toast.success('Tâche créée');
      }
      onSaved?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erreur');
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async () => {
    if (!isEdit) return;
    if (!window.confirm('Supprimer cette tâche ? Action irréversible.')) return;
    setBusy(true);
    try {
      await api.delete(`/tasks/${task.id}`);
      toast.success('Tâche supprimée');
      onDeleted?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erreur');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="admin-todo-task-dialog"
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 120,
        background: 'rgba(6, 12, 26, 0.72)',
        backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)',
        display: 'grid', placeItems: 'center', padding: 18,
      }}
    >
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={submit}
        className="scale-in"
        style={{
          width: '100%', maxWidth: 540,
          background: 'linear-gradient(180deg, rgba(20,30,55,0.98) 0%, rgba(14,22,44,0.98) 100%)',
          borderRadius: 24,
          border: '1px solid rgba(74,134,232,0.18)',
          boxShadow: '0 40px 80px -20px rgba(0,0,0,0.7), inset 0 1px 0 rgba(255,255,255,0.05)',
          padding: 28,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 22 }}>
          <h2 style={{
            margin: 0, fontFamily: 'Sora', fontWeight: 800,
            fontSize: 22, color: '#F1F5FB', letterSpacing: '-0.01em',
            flex: 1,
          }}>{isEdit ? 'Modifier la tâche' : 'Nouvelle tâche'}</h2>
          {isEdit && (
            <button
              type="button"
              data-testid="admin-todo-delete"
              onClick={handleDelete}
              disabled={busy}
              title="Supprimer"
              style={{
                width: 40, height: 40, borderRadius: 12,
                background: 'rgba(255,107,107,0.10)',
                border: '1px solid rgba(255,107,107,0.32)',
                display: 'grid', placeItems: 'center',
                color: '#FF7B7B', cursor: 'pointer',
                transition: 'all 0.15s var(--ease-out)',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255,107,107,0.18)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(255,107,107,0.10)'; }}
            ><Trash2 size={16} /></button>
          )}
        </div>

        {/* Nom */}
        <div style={{ marginBottom: 16 }}>
          <label style={labelStyle}>Nom de la tâche</label>
          <input
            data-testid="admin-todo-task-title"
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Ex. Valider montage épisode 4"
            autoFocus
            style={inputStyle}
          />
        </div>

        {/* Description */}
        <div style={{ marginBottom: 20 }}>
          <label style={labelStyle}>Description</label>
          <textarea
            data-testid="admin-todo-task-desc"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Détail optionnel…"
            rows={3}
            style={{ ...inputStyle, resize: 'vertical', minHeight: 74, fontFamily: 'Sora' }}
          />
        </div>

        {/* Importance */}
        <div style={{ marginBottom: 20 }}>
          <label style={labelStyle}>Importance</label>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {IMPORTANCE.map((i) => {
              const active = importance === i.id;
              const tint = i.id === 'urgent' ? '#FF7B7B' : i.id === 'important' ? '#F5C46A' : '#FF9F5A';
              return (
                <button
                  key={i.id}
                  type="button"
                  data-testid={`admin-todo-importance-${i.id}`}
                  onClick={() => setImportance(i.id)}
                  style={{
                    padding: '9px 18px', borderRadius: 12,
                    background: active ? `${tint}22` : 'transparent',
                    border: `1px solid ${active ? tint : 'rgba(127,173,255,0.20)'}`,
                    color: active ? tint : 'rgba(200,215,240,0.72)',
                    fontFamily: 'Sora', fontWeight: 700, fontSize: 12.5,
                    cursor: 'pointer',
                    transition: 'all 0.15s var(--ease-out)',
                    letterSpacing: '-0.005em',
                  }}
                >{i.label}</button>
              );
            })}
          </div>
        </div>

        {/* Récurrence */}
        <div style={{ marginBottom: 20 }}>
          <label style={labelStyle}>Récurrence</label>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {RECURRENCE.map((r) => {
              const active = recurrence === r.id;
              return (
                <button
                  key={r.id}
                  type="button"
                  data-testid={`admin-todo-recurrence-${r.id}`}
                  onClick={() => setRecurrence(r.id)}
                  style={{
                    padding: '9px 18px', borderRadius: 12,
                    background: active ? 'rgba(74,134,232,0.22)' : 'transparent',
                    border: `1px solid ${active ? '#7FADFF' : 'rgba(127,173,255,0.20)'}`,
                    color: active ? '#F1F5FB' : 'rgba(200,215,240,0.72)',
                    fontFamily: 'Sora', fontWeight: 700, fontSize: 12.5,
                    cursor: 'pointer',
                    transition: 'all 0.15s var(--ease-out)',
                    letterSpacing: '-0.005em',
                  }}
                >{r.label}</button>
              );
            })}
          </div>
          {recurrence !== 'none' && (
            <div style={{
              marginTop: 10, padding: '9px 12px', borderRadius: 10,
              background: 'rgba(127,173,255,0.06)',
              border: '1px solid rgba(127,173,255,0.14)',
              fontFamily: 'Sora', fontSize: 11.5, fontWeight: 500,
              color: 'rgba(200,215,240,0.60)', letterSpacing: '-0.005em',
            }}>
              Une nouvelle occurrence sera automatiquement créée à chaque fois que la tâche est validée.
            </div>
          )}
        </div>

        {/* Assignée à */}
        <div style={{ marginBottom: 24 }}>
          <label style={labelStyle}>Assignée à</label>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            {ASSIGNEES.map((a) => {
              const active = assignee === a.id;
              return (
                <button
                  key={a.id}
                  type="button"
                  data-testid={`admin-todo-assignee-${a.id}`}
                  onClick={() => setAssignee(a.id)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 12,
                    padding: '12px 14px', borderRadius: 14,
                    background: active ? 'rgba(74,134,232,0.14)' : 'rgba(15,25,50,0.55)',
                    border: `1px solid ${active ? '#7FADFF' : 'rgba(127,173,255,0.16)'}`,
                    color: '#F1F5FB',
                    fontFamily: 'Sora', fontWeight: 700, fontSize: 14,
                    cursor: 'pointer',
                    transition: 'all 0.15s var(--ease-out)',
                    textAlign: 'left', letterSpacing: '-0.005em',
                  }}
                >
                  <span
                    aria-hidden
                    style={{
                      width: 22, height: 22, borderRadius: 7, flexShrink: 0,
                      background: active
                        ? 'linear-gradient(180deg, #7FADFF 0%, #4A86E8 100%)'
                        : 'linear-gradient(180deg, rgba(127,173,255,0.35) 0%, rgba(74,134,232,0.20) 100%)',
                      boxShadow: active ? '0 2px 8px -2px rgba(74,134,232,0.7)' : 'none',
                      border: '1px solid rgba(127,173,255,0.30)',
                    }}
                  />
                  {a.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Actions */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.4fr', gap: 12 }}>
          <button
            type="button"
            data-testid="admin-todo-cancel"
            onClick={onClose}
            style={{
              padding: '13px 20px', borderRadius: 14,
              background: 'transparent',
              border: '1px solid rgba(127,173,255,0.22)',
              color: '#F1F5FB',
              fontFamily: 'Sora', fontWeight: 700, fontSize: 13.5,
              cursor: 'pointer',
              transition: 'all 0.15s var(--ease-out)',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#7FADFF'; }}
            onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'rgba(127,173,255,0.22)'; }}
          >Annuler</button>
          <button
            type="submit"
            data-testid="admin-todo-submit"
            disabled={busy}
            style={{
              padding: '13px 20px', borderRadius: 14,
              background: 'linear-gradient(180deg, #7FADFF 0%, #4A86E8 100%)',
              border: 'none',
              color: '#fff',
              fontFamily: 'Sora', fontWeight: 800, fontSize: 13.5,
              cursor: busy ? 'wait' : 'pointer',
              boxShadow: '0 8px 22px -6px rgba(74,134,232,0.65), inset 0 1px 0 rgba(255,255,255,0.28)',
              transition: 'all 0.15s var(--ease-out)',
              letterSpacing: '-0.005em',
              opacity: busy ? 0.7 : 1,
            }}
          >{busy ? (isEdit ? 'Sauvegarde…' : 'Création…') : (isEdit ? 'Sauvegarder' : 'Créer la tâche')}</button>
        </div>
      </form>
    </div>
  );
}

const labelStyle = {
  display: 'block', marginBottom: 8,
  fontFamily: 'Sora', fontWeight: 700, fontSize: 12.5,
  color: 'rgba(200,215,240,0.62)',
  letterSpacing: '-0.005em',
};

const inputStyle = {
  width: '100%',
  padding: '13px 15px', borderRadius: 12,
  background: 'rgba(15,25,50,0.55)',
  border: '1px solid rgba(127,173,255,0.16)',
  color: '#F1F5FB',
  fontFamily: 'Sora', fontWeight: 600, fontSize: 13.5,
  outline: 'none',
  transition: 'border-color 0.15s var(--ease-out)',
  letterSpacing: '-0.005em',
};
